import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { 
  Search, 
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  BarChart3,
  Settings,
  Command,
  ArrowRight,
  Hash
} from 'lucide-react';
import { Dialog, DialogContent } from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { useBranding } from '@/shared/contexts/BrandingContext';
import { useAuth } from '@/shared/hooks/useAuth';

interface CommandItem {
  id: string;
  title: string;
  description?: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  category: string;
  keywords?: string[];
}

const commands: CommandItem[] = [
  // Dashboard
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Business overview and analytics',
    href: '/customer-management',
    icon: LayoutDashboard,
    category: 'Navigation',
    keywords: ['home', 'overview', 'main']
  },
  
  // Customer Management
  {
    id: 'customers',
    title: 'Customer List',
    description: 'Manage your customers and contacts',
    href: '/customer-management?tab=customers',
    icon: Users,
    category: 'Customers',
    keywords: ['clients', 'contacts', 'people']
  },
  {
    id: 'leads',
    title: 'Leads',
    description: 'Manage potential customers',
    href: '/customer-management?tab=leads',
    icon: Users,
    category: 'Customers',
    keywords: ['prospects', 'potential']
  },
  {
    id: 'customer-import',
    title: 'Import Customers',
    description: 'Bulk import customer data',
    href: '/customer-import',
    icon: Users,
    category: 'Customers',
    keywords: ['upload', 'bulk', 'csv']
  },
  
  // Scheduling
  {
    id: 'appointments',
    title: 'Appointments',
    description: 'Schedule and manage appointments',
    href: '/book',
    icon: Calendar,
    category: 'Scheduling',
    keywords: ['schedule', 'booking', 'calendar']
  },
  {
    id: 'service-orders',
    title: 'Service Orders',
    description: 'Manage service requests and orders',
    href: '/customer-management?tab=orders',
    icon: FileText,
    category: 'Scheduling',
    keywords: ['jobs', 'work orders', 'tasks']
  },
  
  // Quotes & Invoices
  {
    id: 'quotation',
    title: 'Create Quote',
    description: 'Generate new customer quotes',
    href: '/quotation',
    icon: FileText,
    category: 'Quotes & Invoices',
    keywords: ['estimate', 'pricing', 'proposal']
  },
  {
    id: 'pricing-admin',
    title: 'Pricing Management',
    description: 'Manage pricing and service rates',
    href: '/pricing-admin',
    icon: Hash,
    category: 'Quotes & Invoices',
    keywords: ['rates', 'prices', 'costs']
  },
  
  // Reporting
  {
    id: 'treatment-reports',
    title: 'Treatment Reports',
    description: 'View and manage treatment documentation',
    href: '/treatment-reports',
    icon: FileText,
    category: 'Reporting',
    keywords: ['documentation', 'compliance', 'records']
  },
  {
    id: 'analytics',
    title: 'Analytics Dashboard',
    description: 'Business intelligence and metrics',
    href: '/analytics-dashboard',
    icon: BarChart3,
    category: 'Reporting',
    keywords: ['metrics', 'insights', 'data']
  },
  {
    id: 'compliance',
    title: 'Compliance Audit',
    description: 'Regulatory compliance tracking',
    href: '/compliance-audit',
    icon: FileText,
    category: 'Reporting',
    keywords: ['regulatory', 'audit', 'legal']
  },
  
  // Settings
  {
    id: 'profile',
    title: 'Company Profile',
    description: 'Manage company information',
    href: '/profile',
    icon: Settings,
    category: 'Settings',
    keywords: ['company', 'business', 'info']
  },
  {
    id: 'access-control',
    title: 'User Management',
    description: 'Manage team access and permissions',
    href: '/access-control',
    icon: Users,
    category: 'Settings',
    keywords: ['team', 'permissions', 'users']
  },
  {
    id: 'workflow-admin',
    title: 'System Settings',
    description: 'Configure system preferences',
    href: '/workflow-admin',
    icon: Settings,
    category: 'Settings',
    keywords: ['configuration', 'preferences', 'admin']
  }
];

export default function CommandPalette() {
  const { isAegis360, currentBrand } = useBranding();
  const { isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Unified access control: Only authenticated Aegis360 users
  const shouldShow = isAegis360 && isAuthenticated && currentBrand;
  
  // Filter commands based on search
  const filteredCommands = commands.filter(command => {
    const searchLower = search.toLowerCase();
    return (
      command.title.toLowerCase().includes(searchLower) ||
      command.description?.toLowerCase().includes(searchLower) ||
      command.category.toLowerCase().includes(searchLower) ||
      command.keywords?.some(keyword => keyword.toLowerCase().includes(searchLower))
    );
  });
  
  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (shouldShow) {
          setIsOpen(true);
        }
      }
      
      // Escape to close
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
        setSelectedIndex(0);
      }
      
      // Arrow navigation when open
      if (isOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < filteredCommands.length - 1 ? prev + 1 : 0
          );
        }
        
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : filteredCommands.length - 1
          );
        }
        
        if (e.key === 'Enter' && filteredCommands[selectedIndex]) {
          e.preventDefault();
          executeCommand(filteredCommands[selectedIndex]);
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredCommands, selectedIndex, shouldShow]);
  
  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);
  
  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  
  const executeCommand = (command: CommandItem) => {
    setLocation(command.href);
    setIsOpen(false);
    setSearch('');
    setSelectedIndex(0);
  };
  
  const groupedCommands = filteredCommands.reduce((acc, command) => {
    if (!acc[command.category]) {
      acc[command.category] = [];
    }
    acc[command.category].push(command);
    return acc;
  }, {} as Record<string, CommandItem[]>);
  
  // Only render for authenticated Aegis360 users
  if (!shouldShow) {
    return null;
  }
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl p-0 gap-0">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center px-4 py-3">
            <Search className="w-4 h-4 text-gray-400 mr-3" />
            <Input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search commands..."
              className="border-0 focus-visible:ring-0 text-sm"
              data-testid="command-search-input"
            />
            <div className="flex items-center gap-1 text-xs text-gray-400 ml-3">
              <Command className="w-3 h-3" />
              <span>K</span>
            </div>
          </div>
        </div>
        
        <ScrollArea className="max-h-96">
          {Object.keys(groupedCommands).length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No commands found</p>
            </div>
          ) : (
            <div className="p-2">
              {Object.entries(groupedCommands).map(([category, items]) => (
                <div key={category} className="mb-4 last:mb-2">
                  <div className="px-2 py-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {category}
                  </div>
                  {items.map((command, index) => {
                    const globalIndex = filteredCommands.indexOf(command);
                    const isSelected = globalIndex === selectedIndex;
                    const Icon = command.icon;
                    
                    return (
                      <button
                        key={command.id}
                        onClick={() => executeCommand(command)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          isSelected 
                            ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' 
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100'
                        }`}
                        data-testid={`command-${command.id}`}
                      >
                        <Icon className="w-4 h-4 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{command.title}</div>
                          {command.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {command.description}
                            </div>
                          )}
                        </div>
                        <ArrowRight className="w-3 h-3 text-gray-400" />
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        <div className="border-t border-gray-200 dark:border-gray-700 p-3">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center gap-4">
              <span>↑↓ Navigate</span>
              <span>↵ Select</span>
              <span>Esc Close</span>
            </div>
            <div className="text-blue-600 font-medium">
              {filteredCommands.length} commands
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}